// Copyright (c) 2025 Goffy. All Rights Reserved.

import {
  ActivityIndicator,
  Modal,
  Pressable,
  ImageBackground,
  type ImageSourcePropType,
  Image,
  ScrollView,
  StyleSheet,
} from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useUser } from '@/app/user-context';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Text, View } from '@/components/Themed';
import GoffyButton from '@/components/GoffyButton';
import { t } from '@/i18n';
import { getGameDisplayName, getSubgameDisplayName } from '@/features/game-definitions/i18n';
import { buildAccessHeaders, appendClaimQuery, hasClaimAccess } from '@/services/claimHttp';
import { getActiveClaimSessionSync, loadClaimSession, subscribeClaimSession } from '@/services/claimSession';
import { useRoundState } from '@/stores/roundState';
import appColors from '@/theme/colors';
import {
  BestBallSelectImage,
  BunkerSelectImage,
  HighLowSelectImage,
  HoleIllustration,
  MysterySelectImage,
  PenaltyIcon,
  SkinsSelectImage,
  StablefordSelectImage,
  ThreePuttIcon,
  TrophyOnlyImage,
  WaterSelectImage,
  WorstBallSelectImage,
} from '@/features/hole/assets';

type Player = { id: string; name: string };
type GameScore = {
  gameId?: string;
  GameId?: string;
  gameName?: string;
  GameName?: string;
  playerId?: string;
  PlayerId?: string;
  scorePoints?: number;
  ScorePoints?: number;
  gamePoints?: number;
  GamePoints?: number;
  roundGamePoints?: number;
  RoundGamePoints?: number;
};
type RoundScoreboardResponse = {
  roundId?: string;
  RoundId?: string;
  scores?: GameScore[];
  Scores?: GameScore[];
};
type HoleEntry = {
  playerId?: string;
  PlayerId?: string;
  hole?: number;
  Hole?: number;
  holeNumber?: number;
  HoleNumber?: number;
  par?: number;
  Par?: number;
  grossStroke?: number | null;
  GrossStroke?: number | null;
  grossStrokes?: number | null;
  GrossStrokes?: number | null;
  pickup?: boolean;
  Pickup?: boolean;
};
type RoundSubgameEntry = {
  playerId?: string;
  PlayerId?: string;
  holeNumber?: number;
  HoleNumber?: number;
  subgameId?: string;
  SubgameId?: string;
};
type HoleEventEntry = {
  playerId?: string;
  PlayerId?: string;
  holeNumber?: number;
  HoleNumber?: number;
  eventType?: string;
  EventType?: string;
  eventValue?: string | null;
  EventValue?: string | null;
};
type GameDefinition = {
  id?: string;
  Id?: string;
  name?: string;
  Name?: string;
};
type Round = {
  id: string;
  name?: string;
  courseName?: string;
  status?: string;
  createdAt: string;
  players: string[];
};

type GameResultRow = {
  playerId: string;
  playerName: string;
  points: number;
};

type GameResultCard = {
  gameId: string;
  gameName: string;
  rows: GameResultRow[];
};

type MysteryHighlight = {
  holeNumber: number;
  mysteryPrize: string;
  subgameName: string;
  winnerName: string;
};

type ParOrBetterRow = {
  playerName: string;
  holes: Array<{ holeNumber: number; resultLabel: string; resultType: 'par' | 'birdie' | 'eagle' | 'albatross' | 'underPar' }>;
};

type HazardByPlayerRow = {
  playerId: string;
  playerName: string;
  total: number;
  water: number;
  bunker: number;
  threePutt: number;
  penalty: number;
};

const GAME_ICON_MAP: Record<string, ImageSourcePropType> = {
  'best-ball': BestBallSelectImage,
  bunker: BunkerSelectImage,
  'high-low': HighLowSelectImage,
  mystery: MysterySelectImage,
  skins: SkinsSelectImage,
  stableford: StablefordSelectImage,
  'stroke-play': StablefordSelectImage,
  water: WaterSelectImage,
  'worst-ball': WorstBallSelectImage,
};

const isStrokePlayGame = (gameId: string | undefined, gameName: string | undefined): boolean => {
  return normalizeGameKey(gameId) === 'stroke-play' || normalizeGameKey(gameName) === 'stroke-play';
};

const normalizeGameKey = (value: string | undefined): string => {
  if (!value) return '';
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

type HazardType = 'water' | 'bunker' | 'threePutt' | 'penalty';

const HAZARD_ICON_MAP: Record<HazardType, ImageSourcePropType> = {
  water: WaterSelectImage,
  bunker: BunkerSelectImage,
  threePutt: ThreePuttIcon,
  penalty: PenaltyIcon,
};

const normalizeHazardType = (value: string): HazardType | null => {
  const normalized = value.replace(/[\s_-]+/g, '').toLowerCase();
  if (normalized === 'water') return 'water';
  if (normalized === 'bunker') return 'bunker';
  if (normalized === 'threeputt') return 'threePutt';
  if (normalized === 'penalty') return 'penalty';
  return null;
};

const getString = (...values: Array<unknown>): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim();
    }
  }
  return undefined;
};

const getNumber = (...values: Array<unknown>): number | undefined => {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
  }
  return undefined;
};

const getBoolean = (...values: Array<unknown>): boolean | undefined => {
  for (const value of values) {
    if (typeof value === 'boolean') {
      return value;
    }
  }
  return undefined;
};

const parseEventValueObject = (value: string | null | undefined): Record<string, unknown> | null => {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) return null;
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (parsed && typeof parsed === 'object') {
      return parsed as Record<string, unknown>;
    }
    return null;
  } catch {
    return null;
  }
};

const getParResult = (
  gross: number,
  par: number
): { resultLabel: string; resultType: 'par' | 'birdie' | 'eagle' | 'albatross' | 'underPar' } => {
  const delta = gross - par;
  if (delta === 0) return { resultLabel: t('summary.resultPar'), resultType: 'par' };
  if (delta === -1) return { resultLabel: t('summary.resultBirdie'), resultType: 'birdie' };
  if (delta === -2) return { resultLabel: t('summary.resultEagle'), resultType: 'eagle' };
  if (delta <= -3) return { resultLabel: t('summary.resultAlbatross'), resultType: 'albatross' };
  return { resultLabel: t('summary.resultUnderPar'), resultType: 'underPar' };
};

export default function RoundSummaryScreen() {
  const { roundId: roundIdParam, tourId: tourIdParam } = useLocalSearchParams<{ roundId?: string; tourId?: string }>();
  const { user } = useUser();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const router = useRouter();
  const storedRoundId = useRoundState((state) => state.roundId);
  const [claimSession, setClaimSession] = useState(() => getActiveClaimSessionSync());
  const [claimSessionLoaded, setClaimSessionLoaded] = useState(false);
  const [round, setRound] = useState<Round | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [scores, setScores] = useState<GameScore[]>([]);
  const [holeEntries, setHoleEntries] = useState<HoleEntry[]>([]);
  const [roundSubgames, setRoundSubgames] = useState<RoundSubgameEntry[]>([]);
  const [holeEvents, setHoleEvents] = useState<HoleEventEntry[]>([]);
  const [gameDefinitions, setGameDefinitions] = useState<GameDefinition[]>([]);
  const [rounds, setRounds] = useState<Round[]>([]);
  const [roundsLoading, setRoundsLoading] = useState(false);
  const [roundsError, setRoundsError] = useState<string | null>(null);
  const [roundPickerOpen, setRoundPickerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const effectiveTourId = useMemo(() => {
    const routeTourId = Array.isArray(tourIdParam) ? tourIdParam[0] : tourIdParam;
    return routeTourId ?? claimSession?.tourId ?? null;
  }, [claimSession?.tourId, tourIdParam]);
  const hasGuestClaimSessionAccess = !!claimSession?.claimClientId && (hasClaimAccess(claimSession) || !!claimSession?.inviteToken?.trim());
  const isGuestClaimMode = !user?.token && hasGuestClaimSessionAccess;
  const effectiveRoundId = useMemo(() => {
    const routeRoundId = Array.isArray(roundIdParam) ? roundIdParam[0] : roundIdParam;
    return routeRoundId ?? claimSession?.startedRoundId ?? storedRoundId ?? null;
  }, [claimSession?.startedRoundId, roundIdParam, storedRoundId]);

  useEffect(() => {
    let cancelled = false;
    loadClaimSession()
      .catch(() => null)
      .finally(() => {
        if (!cancelled) {
          setClaimSession(getActiveClaimSessionSync());
          setClaimSessionLoaded(true);
        }
      });
    const unsubscribe = subscribeClaimSession((nextClaimSession) => {
      if (cancelled) {
        return;
      }

      setClaimSession(nextClaimSession);
      setClaimSessionLoaded(true);
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  const fetchRoundData = useCallback(async () => {
    if (!effectiveRoundId || (!user?.token && !isGuestClaimMode)) {
      setLoading(false);
      setError(t('summary.missingInfo'));
      return;
    }

    try {
      setLoading(true);
      const headers = buildAccessHeaders(user?.token);
      const [roundRes, playersRes, scoreboardRes] = await Promise.all([
        fetch(appendClaimQuery(`${apiBaseUrl}/api/rounds/${effectiveRoundId}`, claimSession), {
          headers,
        }),
        fetch(appendClaimQuery(`${apiBaseUrl}/api/rounds/${effectiveRoundId}/players`, claimSession), {
          headers,
        }),
        fetch(appendClaimQuery(`${apiBaseUrl}/api/rounds/${effectiveRoundId}/scoreboard`, claimSession), {
          headers,
        }),
      ]);

      if (!roundRes.ok || !playersRes.ok || !scoreboardRes.ok) {
        throw new Error(t('summary.loadError'));
      }

      const fetchOptionalJson = async <T,>(
        label: string,
        url: string,
        fallback: T,
      ): Promise<T> => {
        try {
          const response = await fetch(url, { headers });
          if (!response.ok) {
            console.warn('[round-summary] optional fetch failed', {
              label,
              url,
              status: response.status,
            });
            return fallback;
          }

          return (await response.json()) as T;
        } catch (error) {
          console.warn('[round-summary] optional fetch rejected', {
            label,
            url,
            message: error instanceof Error ? error.message : String(error),
          });
          return fallback;
        }
      };

      const [roundData, playersData, scoreboardData, holeEntriesData, subgamesData, holeEventsData, gameDefsData] =
        await Promise.all([
          roundRes.json(),
          playersRes.json(),
          scoreboardRes.json() as Promise<RoundScoreboardResponse>,
          fetchOptionalJson(
            'hole-entries',
            appendClaimQuery(`${apiBaseUrl}/api/rounds/${effectiveRoundId}/hole-entries`, claimSession),
            [] as unknown[],
          ),
          fetchOptionalJson(
            'subgames',
            appendClaimQuery(`${apiBaseUrl}/api/rounds/${effectiveRoundId}/subgames`, claimSession),
            [] as unknown[],
          ),
          fetchOptionalJson(
            'hole-events',
            appendClaimQuery(`${apiBaseUrl}/api/rounds/${effectiveRoundId}/hole-events`, claimSession),
            [] as unknown[],
          ),
          fetchOptionalJson(
            'game-definitions',
            appendClaimQuery(`${apiBaseUrl}/api/game-definitions`, claimSession),
            [] as unknown[],
          ),
        ]);

      const normalizedPlayers = (Array.isArray(playersData) ? playersData : (playersData.players ?? playersData.Players ?? []));
      const normalizedScores = Array.isArray(scoreboardData.scores)
        ? scoreboardData.scores
        : Array.isArray(scoreboardData.Scores)
          ? scoreboardData.Scores
          : [];

      setRound(roundData);
      setPlayers(normalizedPlayers);
      setScores(normalizedScores);
      setHoleEntries(Array.isArray(holeEntriesData) ? holeEntriesData : []);
      setRoundSubgames(Array.isArray(subgamesData) ? subgamesData : []);
      setHoleEvents(Array.isArray(holeEventsData) ? holeEventsData : []);
      setGameDefinitions(Array.isArray(gameDefsData) ? gameDefsData : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('summary.loadError'));
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, claimSession, effectiveRoundId, isGuestClaimMode, user?.token]);

  useEffect(() => {
    if (!user?.token && !claimSessionLoaded) {
      return;
    }
    fetchRoundData();
  }, [claimSessionLoaded, fetchRoundData, user?.token]);

  useEffect(() => {
    if (!effectiveTourId || (!user?.token && !isGuestClaimMode)) {
      setRounds([]);
      setRoundsError(null);
      setRoundsLoading(false);
      return;
    }

    let cancelled = false;
    const loadRounds = async () => {
      setRoundsLoading(true);
      setRoundsError(null);
      try {
        const response = await fetch(appendClaimQuery(`${apiBaseUrl}/api/rounds/tour/${effectiveTourId}`, claimSession), {
          headers: buildAccessHeaders(user?.token),
        });
        if (!response.ok) {
          throw new Error(`${t('scorecard.selectRoundError')} (${response.status})`);
        }

        const payload = (await response.json()) as Round[];
        if (!cancelled) {
          setRounds(payload);
        }
      } catch {
        if (!cancelled) {
          setRoundsError(t('scorecard.selectRoundError'));
        }
      } finally {
        if (!cancelled) {
          setRoundsLoading(false);
        }
      }
    };

    loadRounds();

    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, claimSession, effectiveTourId, isGuestClaimMode, user?.token]);

  const playerById = useMemo(() => {
    const map = new Map<string, Player>();
    players.forEach((player) => map.set(player.id, player));
    return map;
  }, [players]);

  const gameDefinitionNameById = useMemo(() => {
    const map = new Map<string, string>();
    gameDefinitions.forEach((definition) => {
      const id = getString(definition.id, definition.Id);
      const name = getGameDisplayName(
        {
          id,
          name: getString(definition.name, definition.Name),
          info: getString(definition.info, definition.Info),
          description: getString(definition.description, definition.Description),
        },
        t('summary.unknownGame')
      );
      if (id && name) {
        map.set(id.toLowerCase(), name);
      }
    });
    return map;
  }, [gameDefinitions]);

  const playerScores = useMemo(() => {
    const scoresByPlayer: Record<string, { total: number; games: Record<string, number> }> = {};
    players.forEach((player) => {
      scoresByPlayer[player.id] = { total: 0, games: {} };
    });

    scores.forEach((score) => {
      const playerId = getString(score.playerId, score.PlayerId);
      const gameName = getString(score.gameName, score.GameName);
      const points = getNumber(score.scorePoints, score.ScorePoints) ?? 0;
      if (playerId && scoresByPlayer[playerId]) {
        scoresByPlayer[playerId].total += points;
        if (gameName) {
          scoresByPlayer[playerId].games[gameName] = points;
        }
      }
    });

    return players
      .map((player) => ({
        ...player,
        scorePoints: scoresByPlayer[player.id]?.total ?? 0,
        gamePoints: scores
          .filter((score) => getString(score.playerId, score.PlayerId) === player.id)
          .reduce(
            (sum, score) =>
              sum +
              (getNumber(
                score.gamePoints,
                score.GamePoints,
                score.roundGamePoints,
                score.RoundGamePoints
              ) ?? 0),
            0
          ),
        games: scoresByPlayer[player.id]?.games ?? {},
      }))
      .sort((a, b) => b.gamePoints - a.gamePoints || b.scorePoints - a.scorePoints);
  }, [players, scores]);

  const gameResultCards = useMemo(() => {
    const byGame = new Map<string, { gameId: string; gameName: string; playerPoints: Map<string, number> }>();
    scores.forEach((score) => {
      const rawGameId = getString(score.gameId, score.GameId) ?? '';
      const gameName =
        getString(
          score.gameName,
          score.GameName,
          rawGameId ? gameDefinitionNameById.get(rawGameId.toLowerCase()) : undefined
        ) ?? t('summary.unknownGame');
      const key = rawGameId || gameName;
      if (!key) return;
      if (!byGame.has(key)) {
        byGame.set(key, {
          gameId: rawGameId || key,
          gameName,
          playerPoints: new Map<string, number>(),
        });
      }
      const entry = byGame.get(key);
      if (!entry) return;
      if (entry.gameName === t('summary.unknownGame') && gameName !== t('summary.unknownGame')) {
        entry.gameName = gameName;
      }
      const playerId = getString(score.playerId, score.PlayerId);
      const points = getNumber(score.scorePoints, score.ScorePoints) ?? 0;
      if (!playerId) return;
      entry.playerPoints.set(playerId, (entry.playerPoints.get(playerId) ?? 0) + points);
    });

    return Array.from(byGame.values())
      .map((game): GameResultCard => {
        const rows = Array.from(game.playerPoints.entries())
          .map(([playerId, points]) => ({
            playerId,
            playerName: playerById.get(playerId)?.name ?? t('summary.unknownPlayer'),
            points,
          }))
          .sort((a, b) =>
            isStrokePlayGame(game.gameId, game.gameName)
              ? a.points - b.points || a.playerName.localeCompare(b.playerName)
              : b.points - a.points || a.playerName.localeCompare(b.playerName)
          );
        return {
          gameId: game.gameId,
          gameName: game.gameName,
          rows,
        };
      })
      .sort((a, b) => a.gameName.localeCompare(b.gameName));
  }, [scores, playerById, gameDefinitionNameById]);

  const mysteryHighlights = useMemo(() => {
    const winnerByKey = new Map<string, string>();
    holeEvents.forEach((event) => {
      const eventType = getString(event.eventType, event.EventType)?.toLowerCase() ?? '';
      if (!eventType.includes('mystery')) return;
      const parsedValue = parseEventValueObject(getString(event.eventValue, event.EventValue));
      if (!parsedValue) return;
      const subgameId = getString(
        parsedValue['subgameId'],
        parsedValue['SubgameId'],
        parsedValue['gameId'],
        parsedValue['GameId']
      );
      const winnerPlayerId = getString(
        parsedValue['winnerPlayerId'],
        parsedValue['WinnerPlayerId'],
        parsedValue['playerId'],
        parsedValue['PlayerId']
      );
      const holeNumber =
        getNumber(parsedValue['holeNumber'], parsedValue['HoleNumber'], event.holeNumber, event.HoleNumber) ?? 0;
      if (!subgameId || !winnerPlayerId || holeNumber <= 0) return;
      winnerByKey.set(`${holeNumber}:${subgameId}`, winnerPlayerId);
    });

    return roundSubgames
      .map((entry): MysteryHighlight | null => {
        const playerId = getString(entry.playerId, entry.PlayerId);
        const holeNumber = getNumber(entry.holeNumber, entry.HoleNumber) ?? 0;
        const subgameId = getString(entry.subgameId, entry.SubgameId);
        if (!playerId || !subgameId || holeNumber <= 0) return null;
        const key = `${holeNumber}:${subgameId}`;
        const winnerPlayerId = winnerByKey.get(key) ?? playerId;
        const winnerName = playerById.get(winnerPlayerId)?.name ?? t('summary.unknownWinner');
        const subgameName = getSubgameDisplayName(
          {
            id: subgameId,
            name: gameDefinitionNameById.get(subgameId.toLowerCase()),
          },
          t('summary.mysteryPrizeFallback')
        );
        const mysteryPrize = subgameName;
        return {
          holeNumber,
          mysteryPrize,
          subgameName,
          winnerName,
        };
      })
      .filter((entry): entry is MysteryHighlight => entry !== null)
      .sort((a, b) => a.holeNumber - b.holeNumber || a.mysteryPrize.localeCompare(b.mysteryPrize));
  }, [roundSubgames, holeEvents, playerById, gameDefinitionNameById]);

  const parOrBetterByPlayer = useMemo(() => {
    const byPlayer = new Map<string, Array<{ holeNumber: number; resultLabel: string }>>();
    holeEntries.forEach((entry) => {
      const playerId = getString(entry.playerId, entry.PlayerId);
      const holeNumber = getNumber(entry.holeNumber, entry.HoleNumber, entry.hole, entry.Hole) ?? 0;
      const par = getNumber(entry.par, entry.Par);
      const gross = getNumber(entry.grossStroke, entry.GrossStroke, entry.grossStrokes, entry.GrossStrokes);
      const pickup = getBoolean(entry.pickup, entry.Pickup) ?? false;
      if (!playerId || holeNumber <= 0 || par == null || gross == null || pickup) return;
      if (gross <= par) {
        const current = byPlayer.get(playerId) ?? [];
        const result = getParResult(gross, par);
        current.push({
          holeNumber,
          resultLabel: result.resultLabel,
          resultType: result.resultType,
        });
        byPlayer.set(playerId, current);
      }
    });

    return Array.from(byPlayer.entries())
      .map(([playerId, holes]): ParOrBetterRow => ({
        playerName: playerById.get(playerId)?.name ?? t('summary.unknownPlayer'),
        holes: holes.sort((a, b) => a.holeNumber - b.holeNumber),
      }))
      .sort((a, b) => b.holes.length - a.holes.length || a.playerName.localeCompare(b.playerName));
  }, [holeEntries, playerById]);

  const hazardByPlayer = useMemo(() => {
    const byPlayer = new Map<string, HazardByPlayerRow>();

    const getEventPlayerId = (event: HoleEventEntry): string | undefined => {
      const directPlayerId = getString(event.playerId, event.PlayerId);
      if (directPlayerId) return directPlayerId;
      const parsedValue = parseEventValueObject(getString(event.eventValue, event.EventValue));
      return getString(
        parsedValue?.['playerId'],
        parsedValue?.['PlayerId'],
        parsedValue?.['winnerPlayerId'],
        parsedValue?.['WinnerPlayerId']
      );
    };

    holeEvents.forEach((event) => {
      const playerId = getEventPlayerId(event);
      if (!playerId) return;
      const eventType = getString(event.eventType, event.EventType);
      if (!eventType) return;
      const hazardType = normalizeHazardType(eventType);
      if (!hazardType) return;

      const current =
        byPlayer.get(playerId) ??
        {
          playerId,
          playerName: playerById.get(playerId)?.name ?? t('summary.unknownPlayer'),
          total: 0,
          water: 0,
          bunker: 0,
          threePutt: 0,
          penalty: 0,
        };
      current[hazardType] += 1;
      current.total += 1;
      byPlayer.set(playerId, current);
    });

    return Array.from(byPlayer.values())
      .filter((row) => row.total > 0)
      .sort((a, b) => b.total - a.total || a.playerName.localeCompare(b.playerName));
  }, [holeEvents, playerById]);

  const getGameIcon = useCallback((gameName: string) => {
    const normalized = normalizeGameKey(gameName);
    const icon = GAME_ICON_MAP[normalized];
    return icon ?? TrophyOnlyImage;
  }, []);

  const selectedRound = useMemo(
    () => rounds.find((candidate) => candidate.id === effectiveRoundId) ?? round,
    [effectiveRoundId, round, rounds]
  );

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#A1D493" />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.container}>
        <Text style={styles.error}>{error}</Text>
      </View>
    );
  }

  return (
    <ImageBackground source={HoleIllustration} style={styles.pageBackground} imageStyle={styles.pageBackgroundImage}>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <View style={styles.container}>
          <View style={styles.hero}>
            <Text style={styles.title}>{round?.name || t('summary.title')}</Text>
            <Text style={styles.subtitle}>{round?.courseName ?? '-'}</Text>
          </View>

          {effectiveTourId ? (
            <View style={styles.sectionCard}>
              <Text style={styles.sectionTitle}>{t('scorecard.selectRoundTitle')}</Text>
              <Pressable style={styles.roundPicker} onPress={() => setRoundPickerOpen(true)}>
                <Text style={styles.roundPickerText} numberOfLines={1}>
                  {selectedRound?.name ?? selectedRound?.courseName ?? t('scorecard.selectRoundFallback')}
                </Text>
                <Text style={styles.roundPickerHint}>{t('scorecard.selectRound')}</Text>
              </Pressable>
              {roundsLoading ? <Text style={styles.emptyText}>{t('scorecard.selectRoundLoading')}</Text> : null}
              {roundsError ? <Text style={styles.error}>{roundsError}</Text> : null}
              {!roundsLoading && !rounds.length ? <Text style={styles.emptyText}>{t('scorecard.selectRoundEmpty')}</Text> : null}
            </View>
          ) : null}

          <View style={styles.sectionCard}>
            <Text style={styles.sectionTitle}>{t('summary.overallTitle')}</Text>
            <View style={styles.scoreboardHeader}>
              <Text style={[styles.headerText, styles.headerPlayer]}>{t('summary.playerHeader')}</Text>
              <Text style={[styles.headerText, styles.headerValue]}>{t('summary.scoreHeader')}</Text>
              <Text style={[styles.headerText, styles.headerValue]}>{t('summary.gameHeader')}</Text>
            </View>
            {playerScores.map((player, index) => (
              <View key={player.id} style={[styles.playerRow, index === 0 && styles.leaderRow, styles.playerRowCard]}>
                <Text style={styles.playerName}>{player.name}</Text>
                <Text style={styles.playerScore}>{player.scorePoints}</Text>
                <Text style={styles.playerScore}>{player.gamePoints}</Text>
              </View>
            ))}
          </View>

          <View style={styles.sectionCard}>
            <Text style={styles.sectionTitle}>{t('summary.gamesTitle')}</Text>
            {gameResultCards.length === 0 ? (
              <Text style={styles.emptyText}>{t('summary.gamesEmpty')}</Text>
            ) : (
              gameResultCards.map((game) => (
                <View key={`${game.gameId}:${game.gameName}`} style={styles.gameCard}>
                  <View style={styles.gameHeaderRow}>
                    <Image source={getGameIcon(game.gameName)} style={styles.gameIcon} resizeMode="cover" />
                    <Text style={styles.gameTitle}>{game.gameName}</Text>
                  </View>
                  {game.rows.map((row) => (
                    <View key={`${game.gameId}:${row.playerId}`} style={styles.gameRow}>
                      <Text style={styles.gameRowPlayer}>{row.playerName}</Text>
                      <Text style={styles.gameRowPoints}>
                        {isStrokePlayGame(game.gameId, game.gameName)
                          ? t('summary.strokesValue', { strokes: row.points })
                          : t('summary.pointsValue', { points: row.points })}
                      </Text>
                    </View>
                  ))}
                </View>
              ))
            )}
          </View>

          <View style={styles.sectionCard}>
            <Text style={styles.sectionTitle}>{t('summary.hazardsTitle')}</Text>
            {hazardByPlayer.length === 0 ? (
              <Text style={styles.emptyText}>{t('summary.hazardsEmpty')}</Text>
            ) : (
              hazardByPlayer.map((row) => (
                <View key={row.playerId} style={styles.hazardRow}>
                  <View style={styles.hazardHeader}>
                    <Text style={styles.hazardPlayer}>{row.playerName}</Text>
                    <View style={styles.hazardBadge}>
                      <Text style={styles.hazardBadgeText}>{t('summary.hazardTotal', { count: row.total })}</Text>
                    </View>
                  </View>
                  <View style={styles.hazardBadges}>
                    {row.water > 0 ? (
                      <View style={styles.hazardBadge}>
                        <Image source={HAZARD_ICON_MAP.water} style={styles.hazardIcon} resizeMode="cover" />
                        <Text style={styles.hazardBadgeText}>{t('summary.hazardWater', { count: row.water })}</Text>
                      </View>
                    ) : null}
                    {row.bunker > 0 ? (
                      <View style={styles.hazardBadge}>
                        <Image source={HAZARD_ICON_MAP.bunker} style={styles.hazardIcon} resizeMode="cover" />
                        <Text style={styles.hazardBadgeText}>{t('summary.hazardBunker', { count: row.bunker })}</Text>
                      </View>
                    ) : null}
                    {row.threePutt > 0 ? (
                      <View style={styles.hazardBadge}>
                        <Image source={HAZARD_ICON_MAP.threePutt} style={styles.hazardIcon} resizeMode="cover" />
                        <Text style={styles.hazardBadgeText}>{t('summary.hazardThreePutt', { count: row.threePutt })}</Text>
                      </View>
                    ) : null}
                    {row.penalty > 0 ? (
                      <View style={styles.hazardBadge}>
                        <Image source={HAZARD_ICON_MAP.penalty} style={styles.hazardIcon} resizeMode="cover" />
                        <Text style={styles.hazardBadgeText}>{t('summary.hazardPenalty', { count: row.penalty })}</Text>
                      </View>
                    ) : null}
                  </View>
                </View>
              ))
            )}
          </View>

          <View style={styles.sectionCard}>
            <Text style={styles.sectionTitle}>{t('summary.mysteryTitle')}</Text>
            {mysteryHighlights.length === 0 ? (
              <Text style={styles.emptyText}>{t('summary.mysteryEmpty')}</Text>
            ) : (
              mysteryHighlights.map((highlight, index) => (
                <View key={`${highlight.holeNumber}:${highlight.mysteryPrize}:${index}`} style={styles.mysteryRow}>
                  <Image source={MysterySelectImage} style={styles.mysteryIcon} resizeMode="cover" />
                  <View style={styles.mysteryTextBlock}>
                    <Text style={styles.mysteryLine}>
                      {t('summary.mysteryHolePrize', {
                        hole: highlight.holeNumber,
                        prize: highlight.mysteryPrize,
                      })}
                    </Text>
                    <Text style={styles.mysterySubgame}>{t('summary.mysterySubgame', { subgame: highlight.subgameName })}</Text>
                    <Text style={styles.mysteryWinner}>{t('summary.mysteryWinner', { winner: highlight.winnerName })}</Text>
                  </View>
                </View>
              ))
            )}
          </View>

          <View style={styles.sectionCard}>
            <Text style={styles.sectionTitle}>{t('summary.parBetterTitle')}</Text>
            {parOrBetterByPlayer.length === 0 ? (
              <Text style={styles.emptyText}>{t('summary.parBetterEmpty')}</Text>
            ) : (
              parOrBetterByPlayer.map((row) => (
                <View key={row.playerName} style={styles.parRow}>
                  <Text style={styles.parRowPlayer}>{row.playerName}</Text>
                  <View style={styles.parRowBadges}>
                    {row.holes.map((entry) => (
                      <View key={`${row.playerName}-${entry.holeNumber}-${entry.resultType}`} style={styles.parBadge}>
                        <Text style={styles.parBadgeText}>{`H${entry.holeNumber} ${entry.resultLabel}`}</Text>
                        {entry.resultType === 'birdie' ? (
                          <Text style={styles.chickenBirdie}>🐔</Text>
                        ) : null}
                        {entry.resultType === 'eagle' ? (
                          <Text style={styles.eagleIcon}>🦅</Text>
                        ) : null}
                      </View>
                    ))}
                  </View>
                </View>
              ))
            )}
          </View>

          <GoffyButton
            label={t('summary.doneButton')}
            onPress={() => router.push('/')}
            tone="accent"
            style={styles.doneButtonImageMode}
          />
        </View>
      </ScrollView>
      <Modal visible={roundPickerOpen} transparent animationType="fade" onRequestClose={() => setRoundPickerOpen(false)}>
        <Pressable style={styles.modalBackdrop} onPress={() => setRoundPickerOpen(false)}>
          <Pressable style={styles.modalCard} onPress={(event) => event.stopPropagation()}>
            <Text style={styles.sectionTitle}>{t('scorecard.selectRoundTitle')}</Text>
            <ScrollView showsVerticalScrollIndicator={false}>
              <View style={styles.roundPickerOptions}>
                {rounds.map((candidate) => (
                  <Pressable
                    key={candidate.id}
                    style={[
                      styles.roundOption,
                      candidate.id === effectiveRoundId ? styles.roundOptionActive : null,
                    ]}
                    onPress={() => {
                      setRoundPickerOpen(false);
                      router.push(`/round/${candidate.id}/summary?tourId=${effectiveTourId}`);
                    }}
                  >
                    <Text style={styles.roundOptionTitle}>
                      {candidate.name ?? candidate.courseName ?? t('scorecard.selectRoundFallback')}
                    </Text>
                    <Text style={styles.roundOptionSubtitle}>{candidate.status ?? '-'}</Text>
                  </Pressable>
                ))}
              </View>
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>
    </ImageBackground>
  );
}

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#011806',
  },
  pageBackground: {
    flex: 1,
  },
  pageBackgroundImage: {
    opacity: 0.16,
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'flex-start',
  },
  container: {
    flex: 1,
    padding: 18,
    backgroundColor: 'transparent',
    gap: 12,
  },
  hero: {
    backgroundColor: appColors.background.panel,
    borderRadius: 14,
    borderWidth: 2,
    borderColor: '#A1D493',
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  title: {
    fontSize: 30,
    fontWeight: '900',
    color: appColors.text.primary,
    textAlign: 'left',
    fontFamily: 'Sanchez',
  },
  subtitle: {
    fontSize: 14,
    color: appColors.text.primary,
    textAlign: 'left',
    marginTop: 4,
    fontFamily: 'Sanchez',
  },
  sectionCard: {
    backgroundColor: appColors.background.panel,
    borderRadius: 14,
    borderWidth: 2,
    borderColor: '#A1D493',
    paddingHorizontal: 12,
    paddingVertical: 10,
    gap: 8,
  },
  sectionTitle: {
    color: appColors.text.primary,
    fontSize: 17,
    fontWeight: '800',
    fontFamily: 'Sanchez',
  },
  scoreboardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(42,119,66,0.3)',
    paddingBottom: 6,
  },
  headerText: {
    color: appColors.text.primary,
    fontWeight: '800',
    fontSize: 13,
    fontFamily: 'Sanchez',
  },
  headerPlayer: {
    flex: 1,
  },
  headerValue: {
    minWidth: 64,
    textAlign: 'right',
  },
  playerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  playerRowCard: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(42,119,66,0.26)',
    backgroundColor: appColors.background.panelAlt,
  },
  leaderRow: {
    backgroundColor: 'rgba(42,119,66,0.18)',
    borderColor: '#A1D493',
  },
  playerName: {
    color: '#C8C8B0',
    fontSize: 15,
    flex: 1,
    fontFamily: 'Sanchez',
  },
  playerScore: {
    color: appColors.text.primary,
    fontSize: 17,
    fontWeight: '900',
    fontFamily: 'Sanchez',
    minWidth: 64,
    textAlign: 'right',
  },
  gameCard: {
    backgroundColor: appColors.background.panelAlt,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(42,119,66,0.26)',
    padding: 10,
    gap: 6,
  },
  gameHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  gameIcon: {
    width: 30,
    height: 30,
    borderRadius: 6,
  },
  gameTitle: {
    color: '#C8C8B0',
    fontSize: 15,
    fontWeight: '800',
    fontFamily: 'Sanchez',
  },
  gameRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 3,
  },
  gameRowPlayer: {
    color: appColors.text.primary,
    fontSize: 14,
    flex: 1,
    fontFamily: 'Sanchez',
  },
  gameRowPoints: {
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '800',
    fontFamily: 'Sanchez',
  },
  hazardRow: {
    gap: 6,
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(45,98,53,0.28)',
  },
  hazardPlayer: {
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '800',
    fontFamily: 'Sanchez',
  },
  hazardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
  },
  hazardBadges: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  hazardBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: 'rgba(42,119,66,0.28)',
    backgroundColor: 'rgba(16,50,20,0.86)',
  },
  hazardBadgeText: {
    color: appColors.text.primary,
    fontSize: 12,
    fontWeight: '700',
    fontFamily: 'Sanchez',
  },
  hazardIcon: {
    width: 25,
    height: 25,
    borderRadius: 4,
  },
  mysteryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    borderWidth: 1,
    borderColor: 'rgba(42,119,66,0.28)',
    borderRadius: 10,
    paddingHorizontal: 10,
    paddingVertical: 8,
    backgroundColor: appColors.background.panelAlt,
  },
  mysteryIcon: {
    width: 28,
    height: 28,
    borderRadius: 6,
  },
  mysteryTextBlock: {
    flex: 1,
    gap: 2,
  },
  mysteryLine: {
    color: appColors.text.primary,
    fontSize: 13,
    fontWeight: '700',
    fontFamily: 'Sanchez',
  },
  mysteryWinner: {
    color: appColors.text.primary,
    fontSize: 13,
    fontWeight: '800',
    fontFamily: 'Sanchez',
  },
  mysterySubgame: {
    color: appColors.text.primary,
    fontSize: 12,
    fontWeight: '700',
    fontFamily: 'Sanchez',
  },
  parRow: {
    borderWidth: 1,
    borderColor: 'rgba(42,119,66,0.28)',
    borderRadius: 10,
    paddingHorizontal: 10,
    paddingVertical: 8,
    backgroundColor: appColors.background.panelAlt,
    gap: 4,
  },
  parRowPlayer: {
    color: '#C8C8B0',
    fontSize: 14,
    fontWeight: '800',
    fontFamily: 'Sanchez',
  },
  parRowBadges: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  parBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: 'rgba(42,119,66,0.28)',
    backgroundColor: 'rgba(16,50,20,0.86)',
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  parBadgeText: {
    color: appColors.text.primary,
    fontSize: 12,
    fontWeight: '700',
    fontFamily: 'Sanchez',
  },
  chickenBirdie: {
    fontSize: 14,
    lineHeight: 14,
  },
  eagleIcon: {
    fontSize: 14,
    lineHeight: 14,
  },
  emptyText: {
    color: appColors.text.primary,
    fontSize: 13,
    fontFamily: 'Sanchez',
  },
  doneButtonImageMode: {
    width: 220,
    height: 62,
    alignSelf: 'center',
    marginTop: 4,
  },
  error: {
    color: '#fca5a5',
    textAlign: 'center',
  },
  roundPicker: {
    borderWidth: 1,
    borderColor: '#A1D493',
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 12,
    backgroundColor: 'rgba(16,50,20,0.86)',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  roundPickerText: {
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '700',
    flex: 1,
    fontFamily: 'Sanchez',
  },
  roundPickerHint: {
    color: '#A1D493',
    fontSize: 12,
    fontWeight: '700',
    marginLeft: 12,
    fontFamily: 'Sanchez',
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    padding: 20,
  },
  modalCard: {
    backgroundColor: appColors.background.panel,
    borderRadius: 14,
    borderWidth: 2,
    borderColor: '#A1D493',
    padding: 12,
    maxHeight: '80%',
    gap: 10,
  },
  roundPickerOptions: {
    gap: 8,
    backgroundColor: 'transparent',
  },
  roundOption: {
    borderWidth: 1,
    borderColor: 'rgba(42,119,66,0.28)',
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 10,
    backgroundColor: appColors.background.panelAlt,
  },
  roundOptionActive: {
    borderColor: '#A1D493',
    backgroundColor: 'rgba(42,119,66,0.18)',
  },
  roundOptionTitle: {
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '700',
    fontFamily: 'Sanchez',
  },
  roundOptionSubtitle: {
    color: appColors.text.primary,
    fontSize: 12,
    marginTop: 2,
    fontFamily: 'Sanchez',
  },
});
